import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_ENDPOINTS, getNt8TradeAlertWebhookUrl } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { applyControlsPillOrder } from '../utils/vectorBasketDisplay';
import ConnectorApiKeyManager from './ConnectorApiKeyManager';
import {
  normalizeHexColorInput, normalizeScenarioVisibility, SCENARIO_VISIBILITY_OPTIONS, prettyStockAlgoLabel,
} from '../utils/vectorBasketDisplay';
import {
  algosForPortfolioLegTicker,
  defaultAlgoForPortfolioLeg,
  EQUITY_PORTFOLIO_METHODS,
  EQUITY_PORTFOLIO_METHOD_ORDER,
  DEFAULT_EQUITY_PORTFOLIO_METHOD,
  EQUITY_PORTFOLIO_LEVERAGE_MODES,
  EQUITY_PORTFOLIO_LEVERAGE_MODE_ORDER,
  DEFAULT_EQUITY_PORTFOLIO_LEVERAGE_MODE,
  CUSTOM_BLEND_START_BASES,
  CUSTOM_BLEND_START_BASIS_ORDER,
  DEFAULT_CUSTOM_BLEND_START_BASIS,
} from '../utils/vectorCapitalProgramConfig';
import {
  DEFAULT_MONTHLY_RETURN_METHODS,
  MONTHLY_METHOD_CATEGORIES,
  MONTHLY_METHOD_CATEGORY_ORDER,
  MONTHLY_RETURN_METHODS,
  normalizeMonthlyReturnMethods,
} from '../utils/vectorMonthlyReturnMethod';
import {
  BENCHMARK_CATALOG,
  DEFAULT_HISTORY_START,
  normalizeBenchmarkSettings,
} from '../utils/vectorBenchmarkSettings';
import {
  CATEGORY_DATA_TYPES,
  DATA_SOURCE_TYPE_LABELS,
  isWebhookFlavor,
  KAIROS_SYMBOL_OPTIONS,
  resolveScenarioCreateDataSource,
  SCENARIO_CATEGORY_LABELS,
  dataSourceUiKeyFromScenario,
  WEBHOOK_FLAVOR_HINTS,
  slugFromDisplayName,
  supportsNt8LiveOverlayUiKey,
  supportsNt8LiveOverlayStoredType,
} from '../utils/vectorScenarioRegistry';
import {
  makeEmptyNt8OverlaySleeve,
  nt8OverlaySleevesFromConfig,
  validateNt8OverlaySleeves,
  serializeNt8OverlaySleeves,
  mergeNt8OverlayStrategyOptions,
} from '../utils/nt8OverlayConfig';
import { IconCheck, IconChevronDown, IconGrip, IconPlus, IconSettings, IconTrash, IconX, IconZap } from './Icons';
import { Nt8OverlaySleeveBuilder, useNt8AlertStrategies } from './Nt8OverlaySleeveBuilder';

/**
 * Admin / VectorAdmin-only Settings panel. Portfolio Settings edits each Equity
 * Portfolio's equity %, leverage, and per-stock allocation. Scenario Settings
 * chooses which futures, stock options, and individual stock algos appear as
 * selectable strategies (pills + custom composition basket).
 */

const PORTFOLIOS = [
  { key: 'portfolio_1x', title: '1× Portfolio Settings', group: 'equity' },
  { key: 'portfolio_2_25x', title: '2.25× Portfolio Settings', group: 'equity' },
  { key: 'og_portfolio_1x', title: 'OG 1× Portfolio Settings', group: 'og' },
  { key: 'og_portfolio_2_25x', title: 'OG 2.25× Portfolio Settings', group: 'og' },
];

function PortfolioCalendarExplainer() {
  return (
    <div className="veps-callout" role="note">
      <strong>Why Calendar Profits can look different between Equity and OG portfolios</strong>
      <p>
        Both portfolios can show the <em>same stock legs and trade signals</em>, but Overview
        calculates daily dollars differently on purpose:
      </p>
      <ul>
        <li>
          <strong>Equity Portfolio (1× / 2.25×)</strong> — Calendar Profits blends each leg&apos;s
          trades on a <strong>fixed $100k notional</strong> (weighted % returns × $100k). Dollar
          swings usually stay in the hundreds unless a leg has unusually large volume. The growth
          chart and Daily Value tab use a <strong>compounded</strong> blend, so account values
          grow over time while the calendar stays on fixed notional.
        </li>
        <li>
          <strong>OG Portfolio (Fyntrak mirror)</strong> — Calendar Profits, chart, and Daily Value
          all follow Fyntrak&apos;s <strong>compounded account equity curve</strong> (starts at $100k,
          reinvests profits — often $400k+ by 2026). A ~1% day can read as several thousand dollars.
          The Trades tab still lists the same per-leg rows as Equity Portfolio, but those rows are
          <strong> display-only</strong> for OG and will not sum to the calendar.
        </li>
      </ul>
      <p className="veps-callout-foot">
        Percent returns are usually similar; the gap is account sizing (compounded balance vs fixed
        notional). See List of Trades footnotes when comparing OG Trades vs Daily Value.
      </p>
    </div>
  );
}

/**
 * Tab order is also the priority order: the first entry is what the modal opens
 * on, so the most-used tab leads. Strategy Settings is the day-to-day one;
 * portfolio config and Method are occasional, and NT8 Connector is setup-only.
 */
const TABS = [
  { id: 'scenarios', label: 'Strategy Settings' },
  { id: 'create', label: 'Create Strategy' },
  { id: 'portfolio', label: 'Portfolio Settings' },
  { id: 'method', label: 'Method' },
  { id: 'benchmarks', label: 'Benchmark' },
  { id: 'nt8', label: 'NT8 Connector' },
];

function PortfolioEditor({ title, portfolioKey, config, equityAlgoCatalog, onSave, isOgPortfolio = false }) {
  const [equityPct, setEquityPct] = useState(config.equityPct);
  const [leverage, setLeverage] = useState(config.leverage);
  const [legs, setLegs] = useState(config.legs);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setEquityPct(config.equityPct);
    setLeverage(config.leverage);
    setLegs(config.legs);
  }, [config]);

  const resolveLegStrategyName = (leg) => {
    if (leg.strategyName) return leg.strategyName;
    return defaultAlgoForPortfolioLeg(leg.ticker, equityAlgoCatalog, portfolioKey);
  };

  const totalNotional = legs.reduce(
    (sum, l) => sum + (l.enabled ? (Number(l.allocation) || 0) : 0),
    0,
  ) * (Number(leverage) || 0) * ((Number(equityPct) || 0) / 100);

  const updateLeg = (ticker, patch) => {
    setLegs((prev) => prev.map((l) => (l.ticker === ticker ? { ...l, ...patch } : l)));
  };

  const effWeight = (leg) => (leg.enabled
    ? ((Number(equityPct) || 0) / 100) * (Number(leverage) || 0) * (Number(leg.allocation) || 0)
    : 0);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const legsToSave = legs.map((leg) => ({
        ...leg,
        strategyName: resolveLegStrategyName(leg),
      }));
      await onSave({ equityPct: Number(equityPct), leverage: Number(leverage), legs: legsToSave });
      setStatus({ ok: true, msg: 'Saved — Equity Portfolio strategies updated.' });
    } catch (e) {
      setStatus({ ok: false, msg: e.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`veps-portfolio${isOgPortfolio ? ' veps-portfolio--og' : ''}`}>
      <h4 className="veps-portfolio-title">{title}</h4>
      {isOgPortfolio ? (
        <p className="veps-portfolio-note">
          Fyntrak-mirror book: calendar and chart use the compounded equity curve, not per-leg trade
          totals. Trades tab is for reference only.
        </p>
      ) : (
        <p className="veps-portfolio-note">
          Calendar uses fixed $100k notional from leg trades; chart compounds profits on the running
          balance.
        </p>
      )}
      <div className="veps-sliders">
        <label className="veps-slider">
          <span>Equity %</span>
          <input
            type="range"
            min="0"
            max="200"
            step="1"
            value={equityPct}
            onChange={(e) => setEquityPct(Number(e.target.value))}
          />
          <strong>{Number(equityPct).toFixed(0)}%</strong>
        </label>
        <label className="veps-slider">
          <span>Leverage ×</span>
          <input
            type="range"
            min="0"
            max="5"
            step="0.05"
            value={leverage}
            onChange={(e) => setLeverage(Number(e.target.value))}
          />
          <strong>{Number(leverage).toFixed(2)}×</strong>
        </label>
      </div>

      <div className="veps-table-scroll">
        <table className="veps-table">
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Stock</th>
              <th className="veps-num">Alloc %</th>
              <th className="veps-num">Equity / Trade ($)</th>
              <th className="veps-num">Eff. Weight</th>
            </tr>
          </thead>
          <tbody>
            {legs.map((leg) => {
              const algoOptions = algosForPortfolioLegTicker(leg.ticker, equityAlgoCatalog);
              const fallback = defaultAlgoForPortfolioLeg(leg.ticker, equityAlgoCatalog, portfolioKey);
              const selectedAlgo = (leg.strategyName && algoOptions.includes(leg.strategyName))
                ? leg.strategyName
                : ((fallback && algoOptions.includes(fallback)) ? fallback : (algoOptions[0] || ''));
              return (
              <tr key={leg.ticker} className={leg.enabled ? '' : 'veps-row-off'}>
                <td>
                  <input
                    type="checkbox"
                    checked={leg.enabled}
                    onChange={(e) => updateLeg(leg.ticker, { enabled: e.target.checked })}
                    aria-label={`Enable ${leg.ticker}`}
                  />
                </td>
                <td className="veps-algo-cell">
                  <span className="veps-leg-slot">{leg.ticker}</span>
                  {algoOptions.length ? (
                    <select
                      className="veps-algo-select"
                      value={selectedAlgo}
                      onChange={(e) => updateLeg(leg.ticker, { strategyName: e.target.value })}
                      aria-label={`${leg.ticker} stock algo`}
                    >
                      {algoOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="veps-algo-empty">No algos loaded</span>
                  )}
                </td>
                <td className="veps-num">
                  <input
                    type="number"
                    className="veps-input"
                    min="0"
                    step="1"
                    value={leg.allocation}
                    onChange={(e) => updateLeg(leg.ticker, { allocation: Number(e.target.value) })}
                  />
                </td>
                <td className="veps-num">
                  <input
                    type="number"
                    className="veps-input"
                    min="0"
                    step="1000"
                    value={leg.equityPerTrade ?? 0}
                    onChange={(e) => updateLeg(leg.ticker, { equityPerTrade: Number(e.target.value) })}
                    aria-label={`${leg.ticker} equity per trade ($)`}
                  />
                </td>
                <td className="veps-num veps-eff">{effWeight(leg).toFixed(1)}%</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="veps-footer">
        <span className="veps-total">
          Total notional: <strong>{totalNotional.toFixed(1)}%</strong> · Effective leverage:{' '}
          <strong>{(Number(leverage) * ((Number(equityPct) || 0) / 100)).toFixed(2)}×</strong>
        </span>
        <div className="veps-save-wrap">
          {status ? (
            <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span>
          ) : null}
          <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save to Performance Tab'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScenarioOptionRow({
  visibilityValue,
  onVisibilityChange,
  labelValue,
  onLabelChange,
  colorValue,
  onColorChange,
  defaultLabel,
  slugName,
  onEdit,
  showEditButton = false,
  onNt8Overlay,
  showNt8OverlayButton = false,
  showInCustom = true,
  onShowInCustomChange,
}) {
  const visibility = normalizeScenarioVisibility(visibilityValue);
  // Under the name field we show the SLUG (the immutable registry key), not the
  // display name — renaming a strategy changes the label above, so echoing the
  // label here was just the same string twice. The slug is what Strategy Data,
  // the scenario editor, webhooks and the API all key on, so having it visible
  // at list level makes "which row is this really?" answerable without opening
  // the settings drawer. Non-registry rows key on their catalog name, which is
  // still the right identifier to show.
  const showSlug = Boolean(slugName);
  return (
    <div className="veps-scenario-item">
      <input
        type="color"
        className="veps-scenario-color-input"
        value={normalizeHexColorInput(colorValue)}
        aria-label={`Color for ${defaultLabel}`}
        title="Strategy color"
        onChange={(e) => onColorChange(e.target.value)}
      />
      <div className="veps-scenario-label-wrap">
        <input
          type="text"
          className="veps-scenario-label-input"
          value={labelValue}
          placeholder={defaultLabel}
          aria-label={`Display name for ${defaultLabel}`}
          onChange={(e) => onLabelChange(e.target.value)}
        />
        {showSlug ? (
          <span className="veps-scenario-orig" title={`Slug: ${slugName}`}>{slugName}</span>
        ) : null}
      </div>
      {showEditButton && onEdit ? (
        <button
          type="button"
          className="veps-scenario-edit-btn"
          onClick={onEdit}
          aria-label={`Edit strategy ${defaultLabel}`}
          title="Edit strategy settings"
        >
          <IconSettings size={16} color="currentColor" />
        </button>
      ) : null}
      {showNt8OverlayButton && onNt8Overlay ? (
        <button
          type="button"
          className="veps-scenario-edit-btn veps-scenario-nt8-btn"
          onClick={onNt8Overlay}
          aria-label={`Configure NT8 live overlay for ${defaultLabel}`}
          title="Configure NT8 live overlay (CSV + webhooks)"
        >
          <IconZap size={16} color="currentColor" />
        </button>
      ) : null}
      {onShowInCustomChange ? (
        <label
          className={`veps-custom-toggle${showInCustom ? ' active' : ''}`}
          title="Show this strategy in the Custom composition grid (independent of Public / Internal / Hidden)"
        >
          <input
            type="checkbox"
            className="veps-custom-toggle-input"
            checked={showInCustom !== false}
            onChange={(e) => onShowInCustomChange(e.target.checked)}
            aria-label={`Show ${defaultLabel} in custom options`}
          />
          <span>Custom</span>
        </label>
      ) : null}
      <div className="veps-visibility-group" role="group" aria-label={`Visibility for ${defaultLabel}`}>
        {SCENARIO_VISIBILITY_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`veps-visibility-btn veps-visibility-btn--${opt.id}${visibility === opt.id ? ' active' : ''}`}
            aria-pressed={visibility === opt.id}
            onClick={() => onVisibilityChange(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function visibilityFromAdminRow(row) {
  if (row?.visibility) return normalizeScenarioVisibility(row.visibility);
  return row?.enabled ? 'public' : 'hidden';
}

function normalizeDisplayLabelForSave(draftValue, defaultLabel) {
  const raw = String(draftValue ?? defaultLabel).trim();
  const baseline = String(defaultLabel || '').trim();
  if (!raw || raw === baseline) return null;
  return raw;
}

function normalizeDisplayColorForSave(draftValue, defaultColor) {
  const normalizedDraft = normalizeHexColorInput(draftValue, defaultColor);
  const normalizedDefault = normalizeHexColorInput(defaultColor);
  if (normalizedDraft === normalizedDefault) return null;
  return normalizedDraft;
}

function Nt8AlertStrategyMappingFields({
  nt8StrategyName,
  onNt8StrategyNameChange,
  mappedFrom,
  strategies,
  loading,
  loadError,
}) {
  return (
    <>
      <label className="veps-span-2">
        <span>NT8 futures strategy (from webhooks)</span>
        <select
          value={nt8StrategyName}
          onChange={(e) => onNt8StrategyNameChange(e.target.value)}
          disabled={loading}
        >
          <option value="">Select strategy from incoming alerts…</option>
          {strategies.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        {loading ? <small>Loading strategies from NT8 alerts…</small> : null}
        {!loading && !strategies.length ? (
          <small>No NT8 Trade Alert strategies yet — send a test webhook first.</small>
        ) : null}
        {loadError ? <small className="veps-status err">{loadError}</small> : null}
      </label>
      {mappedFrom ? (
        <p className="veps-scenario-hint veps-span-2">
          Live NT8 trades from <strong>{mappedFrom}</strong> forward (or after your last CSV trade, whichever is later). Changing the mapped strategy resets the connect date.
        </p>
      ) : nt8StrategyName ? (
        <p className="veps-scenario-hint veps-span-2">
          Upload an optional TradesViz CSV for history, then map a live NT8 strategy — webhooks stitch on after the CSV ends.
        </p>
      ) : null}
    </>
  );
}

function ScenarioNt8OverlayEditor({ slug, onCancel, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [dataSourceType, setDataSourceType] = useState('trade_journal');
  const [strategyName, setStrategyName] = useState('');
  const [nt8OverlaySleeves, setNt8OverlaySleeves] = useState([makeEmptyNt8OverlaySleeve()]);
  const [mappedFrom, setMappedFrom] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef(null);
  const nt8AlertStrategies = useNt8AlertStrategies(true);
  const nt8StrategyOptions = useMemo(
    () => mergeNt8OverlayStrategyOptions(nt8AlertStrategies.strategies, nt8OverlaySleeves),
    [nt8AlertStrategies.strategies, nt8OverlaySleeves],
  );
  const nt8TradeAlertWebhookUrl = getNt8TradeAlertWebhookUrl();
  const csvLabel = dataSourceType === 'equity_curve'
    ? 'Replace equity CSV (historical baseline, optional)'
    : 'Replace TradesViz CSV (historical baseline, optional)';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(API_ENDPOINTS.adminScenarioBySlug(slug), {
          headers: getBearerAuthHeaders(),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.success || !data.scenario) {
          throw new Error(data.error || 'Failed to load strategy');
        }
        if (cancelled) return;
        const s = data.scenario;
        const config = s.dataSourceConfig || {};
        setDisplayName(s.displayName || '');
        setDataSourceType(s.dataSourceType || 'trade_journal');
        setStrategyName(config.strategyName || s.displayName || '');
        setNt8OverlaySleeves(nt8OverlaySleevesFromConfig(config));
        setMappedFrom(config.mappedFrom || '');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load strategy');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  };

  const handleSave = async () => {
    const validation = validateNt8OverlaySleeves(nt8OverlaySleeves);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const resp = await fetch(API_ENDPOINTS.adminScenarioNt8LiveOverlay(slug), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          nt8OverlaySleeves: serializeNt8OverlaySleeves(nt8OverlaySleeves),
          csv: csvText.trim() || undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Failed to save NT8 overlay');
      if (data.scenario?.dataSourceConfig?.mappedFrom) {
        setMappedFrom(data.scenario.dataSourceConfig.mappedFrom);
      }
      setStatus({ ok: true, msg: 'Saved NT8 live overlay — CSV history stays; new webhook trades stitch on after the last CSV trade.' });
      if (onSaved) onSaved(data.scenario);
    } catch (err) {
      setError(err.message || 'Failed to save NT8 overlay');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-scenario-edit-panel">
      <div className="veps-scenario-edit-head">
        <div>
          <h4>NT8 live overlay</h4>
          <p className="veps-scenario-edit-slug">
            <code>{slug}</code>
            {displayName ? <> · {displayName}</> : null}
          </p>
        </div>
        <button type="button" className="veps-scenario-edit-back" onClick={onCancel}>
          Back to list
        </button>
      </div>
      {loading ? (
        <p className="veps-scenario-intro">Loading strategy…</p>
      ) : (
        <>
          {error ? <p className="veps-scenario-error">{error}</p> : null}
          <p className="veps-scenario-hint">
            Add live NT8 Trade Alert webhooks on top of this scenario&apos;s existing CSV baseline.
            Your primary data source stays unchanged — webhooks stitch on after the last CSV point or trade.
          </p>
          <div className="veps-scenario-creator-grid">
            <label className="veps-span-2">
              <span>Strategy / data name (read-only)</span>
              <input type="text" value={strategyName} readOnly disabled className="veps-input-readonly" />
            </label>
            <Nt8OverlaySleeveBuilder
              sleeves={nt8OverlaySleeves}
              onSleevesChange={setNt8OverlaySleeves}
              strategyOptions={nt8StrategyOptions}
              loading={nt8AlertStrategies.loading}
              loadError={nt8AlertStrategies.loadError}
              mappedFrom={mappedFrom}
            />
            <label className="veps-span-2">
              <span>{csvLabel}</span>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile} />
              {csvFileName ? <small>{csvFileName}</small> : null}
            </label>
            <div className="veps-span-2 veps-scenario-webhook-block">
              <span className="veps-scenario-webhook-label">NT8 webhook URL</span>
              <p className="veps-scenario-hint">
                Point NinjaTrader Trade Alert at this URL. Use your Fyntrak webhook secret in the JSON body
                as <code>secret</code> or <code>token</code> (same token as other RiskLo webhooks for your account).
              </p>
              <p className="veps-scenario-webhook-url">
                <code>{nt8TradeAlertWebhookUrl}</code>
              </p>
              <p className="veps-scenario-hint">
                Body format: <code>{'{"username":"Trade Alert","content":"..."}'}</code>
                {' '}Entry and exit alerts arrive as separate POSTs. Only trades for your mapped strategy
                name are included, from the connect date forward (or after the last CSV trade).
              </p>
            </div>
          </div>
          <div className="veps-scenario-actions">
            {status ? (
              <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span>
            ) : null}
            <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save NT8 overlay'}
            </button>
            {strategyName ? (
              <a
                className="veps-scenario-data-link"
                href={`/admin/scenario-data?scenario=${encodeURIComponent(slug)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Strategy Data
              </a>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

function mergeNt8OverlayIntoConfig(baseConfig, sleeves) {
  const serialized = serializeNt8OverlaySleeves(sleeves);
  const out = { ...baseConfig, nt8OverlaySleeves: serialized };
  if (!serialized.length) {
    delete out.nt8StrategyName;
  }
  return out;
}

function buildNt8AlertDataSourceConfig(displayName, nt8StrategyName) {
  const nt8 = String(nt8StrategyName || '').trim();
  return {
    strategyName: displayName.trim() || nt8,
    ...(nt8 ? { nt8StrategyName: nt8 } : {}),
  };
}

function ScenarioCreatorEditor({
  futuresCatalogNames,
  equityAlgoCatalog,
  onCreated,
}) {
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [category, setCategory] = useState('tests');
  const [dataSourceType, setDataSourceType] = useState('equity_curve');
  const [visibility, setVisibility] = useState('internal');
  const [description, setDescription] = useState('');
  const [riskLabel, setRiskLabel] = useState('');
  const [displayColor, setDisplayColor] = useState('#8b5cf6');
  const [strategyName, setStrategyName] = useState('');
  const [portfolioKey, setPortfolioKey] = useState('');
  const [webhookStrategyId, setWebhookStrategyId] = useState('');
  const [kairosSymbol, setKairosSymbol] = useState('QLD');
  const [nt8StrategyName, setNt8StrategyName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [equityColumn, setEquityColumn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const nt8AlertStrategies = useNt8AlertStrategies(dataSourceType === 'webhook_nt8_alert');
  const nt8StrategyOptions = useMemo(() => {
    const set = new Set(nt8AlertStrategies.strategies);
    const current = String(nt8StrategyName || '').trim();
    if (current) set.add(current);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [nt8AlertStrategies.strategies, nt8StrategyName]);

  const allowedDataTypes = useMemo(
    () => CATEGORY_DATA_TYPES[category] || [],
    [category],
  );

  useEffect(() => {
    if (!allowedDataTypes.includes(dataSourceType)) {
      setDataSourceType(allowedDataTypes[0] || 'equity_curve');
    }
  }, [category, allowedDataTypes, dataSourceType]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugFromDisplayName(displayName));
  }, [displayName, slugTouched]);

  useEffect(() => {
    if (!strategyName && displayName) setStrategyName(displayName.trim());
  }, [displayName, strategyName]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  };

  const buildDataSourceConfig = () => {
    const slugKey = slug.trim() || slugFromDisplayName(displayName);
    const name = strategyName.trim() || displayName.trim();
    if (dataSourceType === 'portfolio_blend') {
      return { portfolioKey: portfolioKey || slugKey, presetId: slugKey };
    }
    if (dataSourceType === 'futures_snapshot') return { strategyName: name };
    if (dataSourceType === 'webhook_tradingview') {
      const config = { strategyName: name };
      const sid = String(webhookStrategyId || '').trim();
      if (sid) config.strategyId = sid;
      return config;
    }
    if (dataSourceType === 'webhook_kairos') {
      // Empty kairosSymbol = "no symbol" (mixed/custom products) — the
      // strategy URL alone routes the daily-equity webhook.
      return { strategyName: name, kairosSymbol };
    }
    if (dataSourceType === 'webhook_fyntrak') {
      return { strategyName: name };
    }
    if (dataSourceType === 'webhook_nt8_alert') {
      return buildNt8AlertDataSourceConfig(displayName, nt8StrategyName);
    }
    return { strategyName: name };
  };

  const webhookHint = WEBHOOK_FLAVOR_HINTS[dataSourceType] || null;
  const showStrategyNameField = (
    dataSourceType === 'equity_curve'
    || dataSourceType === 'trade_journal'
    || dataSourceType === 'futures_snapshot'
    || (isWebhookFlavor(dataSourceType) && dataSourceType !== 'webhook_nt8_alert')
  );

  const handleCreate = async () => {
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const resolved = resolveScenarioCreateDataSource(dataSourceType, buildDataSourceConfig());
      const resp = await fetch(API_ENDPOINTS.adminScenarios, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          displayName: displayName.trim(),
          slug: slug.trim() || undefined,
          category,
          dataSourceType: resolved.dataSourceType,
          visibility,
          description: description.trim() || null,
          riskLabel: riskLabel.trim() || null,
          displayColor,
          dataSourceConfig: resolved.dataSourceConfig,
          csv: csvText.trim() || undefined,
          equityColumn: equityColumn.trim() || undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Failed to create strategy');
      setResult(data);
      if (onCreated) onCreated(data.scenario);
      setDisplayName('');
      setSlug('');
      setSlugTouched(false);
      setDescription('');
      setRiskLabel('');
      setCsvText('');
      setCsvFileName('');
      setWebhookStrategyId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.message || 'Failed to create strategy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-scenario-creator">
      <p className="veps-scenario-intro">
        Create a new strategy without code changes. It appears on the dashboard
        (per visibility) and in Strategy Data for CSV / trade uploads.
        To attach a live NinjaTrader account later, use the <strong>NT8 Connector</strong> tab.
      </p>
      {error ? <p className="veps-scenario-error">{error}</p> : null}
      {result?.scenario ? (
        <div className="veps-scenario-create-result">
          <p className="veps-status ok">
            Created “{result.scenario.displayName}”.
            {result.webhookUrl ? ` Webhook URL generated.` : ''}
          </p>
          {result.webhookUrl ? (
            <p className="veps-scenario-webhook-url">
              <code>{result.webhookUrl}</code>
            </p>
          ) : null}
          <p className="veps-scenario-create-links">
            Open in{' '}
            <a href={`/admin/scenario-data?scenario=${encodeURIComponent(result.scenario.slug)}`}>
              Scenario Data
            </a>
          </p>
        </div>
      ) : null}
      <div className="veps-scenario-creator-grid">
        <label>
          <span>Display name</span>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          <span>Slug (stable id)</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
          />
        </label>
        <label>
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {Object.entries(SCENARIO_CATEGORY_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Data source</span>
          <select value={dataSourceType} onChange={(e) => setDataSourceType(e.target.value)}>
            {allowedDataTypes.map((id) => (
              <option key={id} value={id}>{DATA_SOURCE_TYPE_LABELS[id] || id}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Visibility</span>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
            {SCENARIO_VISIBILITY_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Color</span>
          <input type="color" value={displayColor} onChange={(e) => setDisplayColor(e.target.value)} />
        </label>
        <label className="veps-span-2">
          <span>Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="veps-span-2">
          <span>Risk label (subtitle on dashboard)</span>
          <input type="text" value={riskLabel} onChange={(e) => setRiskLabel(e.target.value)} />
        </label>
        {(showStrategyNameField) ? (
          <label className="veps-span-2">
            <span>{dataSourceType === 'futures_snapshot' ? 'Futures strategy name' : 'Strategy / data name'}</span>
            {dataSourceType === 'futures_snapshot' ? (
              <select value={strategyName} onChange={(e) => setStrategyName(e.target.value)}>
                <option value="">Select futures strategy…</option>
                {(futuresCatalogNames || []).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            ) : (
              <input type="text" value={strategyName} onChange={(e) => setStrategyName(e.target.value)} />
            )}
          </label>
        ) : null}
        {dataSourceType === 'webhook_tradingview' ? (
          <label className="veps-span-2">
            <span>TradingView strategy_id (optional)</span>
            <input
              type="text"
              value={webhookStrategyId}
              onChange={(e) => setWebhookStrategyId(e.target.value)}
              placeholder="e.g. BTCTest or 999"
            />
          </label>
        ) : null}
        {dataSourceType === 'webhook_kairos' ? (
          <label className="veps-span-2">
            <span>Kairos symbol</span>
            <select value={kairosSymbol} onChange={(e) => setKairosSymbol(e.target.value)}>
              {KAIROS_SYMBOL_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </label>
        ) : null}
        {dataSourceType === 'webhook_nt8_alert' ? (
          <Nt8AlertStrategyMappingFields
            nt8StrategyName={nt8StrategyName}
            onNt8StrategyNameChange={setNt8StrategyName}
            mappedFrom={null}
            strategies={nt8StrategyOptions}
            loading={nt8AlertStrategies.loading}
            loadError={nt8AlertStrategies.loadError}
          />
        ) : null}
        {isWebhookFlavor(dataSourceType) ? (
          <p className="veps-scenario-hint veps-span-2">{webhookHint}</p>
        ) : null}
        {dataSourceType === 'portfolio_blend' ? (
          <label className="veps-span-2">
            <span>Portfolio config key (defaults to slug for new blends)</span>
            <input type="text" value={portfolioKey} onChange={(e) => setPortfolioKey(e.target.value)} placeholder="Auto from slug if left as template key" />
          </label>
        ) : null}
        {(dataSourceType === 'equity_curve' || dataSourceType === 'trade_journal' || dataSourceType === 'webhook_kairos' || dataSourceType === 'webhook_nt8_alert') ? (
          <>
            <label className="veps-span-2">
              <span>{dataSourceType === 'webhook_nt8_alert' ? 'Optional TradesViz CSV (historical baseline)' : 'Optional CSV on create'}</span>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile} />
              {csvFileName ? <small>{csvFileName}</small> : null}
            </label>
            {dataSourceType === 'equity_curve' || dataSourceType === 'webhook_kairos' ? (
              <label>
                <span>Equity column (optional)</span>
                <input type="text" value={equityColumn} onChange={(e) => setEquityColumn(e.target.value)} />
              </label>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="veps-scenario-actions">
        <button type="button" className="veps-save-btn" onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating…' : 'Create strategy'}
        </button>
      </div>
    </div>
  );
}

function ScenarioEditEditor({
  slug,
  futuresCatalogNames,
  onCancel,
  onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('tests');
  const [dataSourceType, setDataSourceType] = useState('equity_curve');
  const [visibility, setVisibility] = useState('internal');
  const [description, setDescription] = useState('');
  const [riskLabel, setRiskLabel] = useState('');
  const [displayColor, setDisplayColor] = useState('#8b5cf6');
  const [strategyName, setStrategyName] = useState('');
  const [loadedDataStrategyName, setLoadedDataStrategyName] = useState('');
  const [portfolioKey, setPortfolioKey] = useState('');
  const [webhookStrategyId, setWebhookStrategyId] = useState('');
  const [kairosSymbol, setKairosSymbol] = useState('QLD');
  const [nt8StrategyName, setNt8StrategyName] = useState('');
  const [nt8OverlaySleeves, setNt8OverlaySleeves] = useState([makeEmptyNt8OverlaySleeve()]);
  const [mappedFrom, setMappedFrom] = useState('');
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [equityColumn, setEquityColumn] = useState('');
  const [isNtConnector, setIsNtConnector] = useState(false);
  const [isSystemScenario, setIsSystemScenario] = useState(false);
  const fileInputRef = useRef(null);
  const showNt8OverlayFields = supportsNt8LiveOverlayUiKey(dataSourceType);
  const nt8AlertStrategies = useNt8AlertStrategies(showNt8OverlayFields || dataSourceType === 'webhook_nt8_alert');
  const nt8StrategyOptions = useMemo(
    () => mergeNt8OverlayStrategyOptions(nt8AlertStrategies.strategies, nt8OverlaySleeves),
    [nt8AlertStrategies.strategies, nt8OverlaySleeves],
  );

  const allowedDataTypes = useMemo(() => {
    const base = CATEGORY_DATA_TYPES[category] || [];
    return isNtConnector ? ['nt_connector', ...base.filter((id) => id !== 'nt_connector')] : base;
  }, [category, isNtConnector]);

  useEffect(() => {
    if (!allowedDataTypes.includes(dataSourceType)) {
      setDataSourceType(allowedDataTypes[0] || 'equity_curve');
    }
  }, [category, allowedDataTypes, dataSourceType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(API_ENDPOINTS.adminScenarioBySlug(slug), {
          headers: getBearerAuthHeaders(),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.success || !data.scenario) {
          throw new Error(data.error || 'Failed to load strategy');
        }
        if (cancelled) return;
        const s = data.scenario;
        const config = s.dataSourceConfig || {};
        const nt = s.dataSourceType === 'nt_connector';
        setIsNtConnector(nt);
        setIsSystemScenario(Boolean(s.isSystem));
        setDisplayName(s.displayName || '');
        setCategory(s.category || 'tests');
        setDataSourceType(nt ? 'nt_connector' : dataSourceUiKeyFromScenario(s));
        setVisibility(s.visibility || 'internal');
        setDescription(s.description || '');
        setRiskLabel(s.riskLabel || '');
        setDisplayColor(s.displayColor || '#8b5cf6');
        setStrategyName(config.strategyName || s.displayName || '');
        setLoadedDataStrategyName(config.strategyName || '');
        setNt8StrategyName(config.nt8StrategyName || '');
        setNt8OverlaySleeves(nt8OverlaySleevesFromConfig(config));
        setMappedFrom(config.mappedFrom || '');
        setPortfolioKey(config.portfolioKey || '');
        setWebhookStrategyId(config.strategyId || '');
        setKairosSymbol(config.kairosSymbol || '');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load strategy');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
  };

  const buildDataSourceConfig = () => {
    const name = strategyName.trim() || loadedDataStrategyName.trim() || displayName.trim();
    if (dataSourceType === 'portfolio_blend') {
      return { portfolioKey: portfolioKey || slug, presetId: slug };
    }
    if (dataSourceType === 'futures_snapshot') {
      return mergeNt8OverlayIntoConfig({ strategyName: name }, nt8OverlaySleeves);
    }
    if (dataSourceType === 'webhook_tradingview') {
      const config = { strategyName: name };
      const sid = String(webhookStrategyId || '').trim();
      if (sid) config.strategyId = sid;
      return mergeNt8OverlayIntoConfig(config, nt8OverlaySleeves);
    }
    if (dataSourceType === 'webhook_kairos') {
      return mergeNt8OverlayIntoConfig(
        // Empty kairosSymbol = "no symbol" (mixed/custom products).
        { strategyName: name, kairosSymbol },
        nt8OverlaySleeves,
      );
    }
    if (dataSourceType === 'webhook_fyntrak') {
      return mergeNt8OverlayIntoConfig({ strategyName: name }, nt8OverlaySleeves);
    }
    if (dataSourceType === 'webhook_nt8_alert') {
      return buildNt8AlertDataSourceConfig(displayName, nt8StrategyName);
    }
    return mergeNt8OverlayIntoConfig({ strategyName: name }, nt8OverlaySleeves);
  };

  const webhookHint = WEBHOOK_FLAVOR_HINTS[dataSourceType] || null;
  const showStrategyNameField = (
    dataSourceType === 'equity_curve'
    || dataSourceType === 'trade_journal'
    || dataSourceType === 'futures_snapshot'
    || (isWebhookFlavor(dataSourceType) && dataSourceType !== 'webhook_nt8_alert')
  );
  const canReplaceCsv = (
    dataSourceType === 'equity_curve'
    || dataSourceType === 'trade_journal'
    || dataSourceType === 'webhook_kairos'
    || dataSourceType === 'webhook_nt8_alert'
  );

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    if (showNt8OverlayFields) {
      const started = nt8OverlaySleeves.some((s) => String(s.strategyName || '').trim());
      if (started) {
        const overlayValidation = validateNt8OverlaySleeves(nt8OverlaySleeves);
        if (overlayValidation) {
          setError(overlayValidation);
          return;
        }
      }
    }
    if (isNtConnector && dataSourceType === 'nt_connector') {
      setSaving(true);
      setError(null);
      setStatus(null);
      try {
        const resp = await fetch(API_ENDPOINTS.adminScenarioBySlug(slug), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
          body: JSON.stringify({
            displayName: displayName.trim(),
            category,
            visibility,
            description: description.trim() || null,
            riskLabel: riskLabel.trim() || null,
            displayColor,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.success) throw new Error(data.error || 'Failed to update strategy');
        setStatus({ ok: true, msg: 'Saved strategy settings.' });
        if (onSaved) onSaved(data.scenario);
      } catch (err) {
        setError(err.message || 'Failed to update strategy');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const resolved = resolveScenarioCreateDataSource(dataSourceType, buildDataSourceConfig());
      const resp = await fetch(API_ENDPOINTS.adminScenarioBySlug(slug), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          displayName: displayName.trim(),
          category,
          dataSourceType: resolved.dataSourceType,
          visibility,
          description: description.trim() || null,
          riskLabel: riskLabel.trim() || null,
          displayColor,
          dataSourceConfig: resolved.dataSourceConfig,
          csv: csvText.trim() || undefined,
          equityColumn: equityColumn.trim() || undefined,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Failed to update strategy');
      if (data.scenario?.dataSourceConfig?.mappedFrom) {
        setMappedFrom(data.scenario.dataSourceConfig.mappedFrom);
      }
      setStatus({ ok: true, msg: 'Saved strategy settings.' });
      if (onSaved) onSaved(data.scenario);
    } catch (err) {
      setError(err.message || 'Failed to update strategy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-scenario-edit-panel">
      <div className="veps-scenario-edit-head">
        <div>
          <h4>Edit strategy</h4>
          <p className="veps-scenario-edit-slug">
            <code>{slug}</code>
          </p>
        </div>
        <button type="button" className="veps-scenario-edit-back" onClick={onCancel}>
          Back to list
        </button>
      </div>
      {loading ? (
        <p className="veps-scenario-intro">Loading strategy…</p>
      ) : (
        <>
          {error ? <p className="veps-scenario-error">{error}</p> : null}
          {isSystemScenario ? (
            <p className="veps-scenario-hint">
              This is a built-in system strategy — you can update display fields, CSV data, and NT8 overlay settings.
            </p>
          ) : null}
          {isNtConnector && dataSourceType === 'nt_connector' ? (
            <p className="veps-scenario-hint">
              This scenario uses <strong>NT8 Connector (live sync)</strong>. Account mapping is managed in the
              {' '}<strong>NT8 Connector</strong> tab. You can update display fields here, or pick a different
              data source below to switch away from live sync (that clears the NT8 account binding).
            </p>
          ) : null}
          <div className="veps-scenario-creator-grid">
            <label>
              <span>Display name</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label>
              <span>Slug (read-only)</span>
              <input type="text" value={slug} readOnly disabled className="veps-input-readonly" />
            </label>
            <label>
              <span>Category</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(SCENARIO_CATEGORY_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Data source</span>
              <select value={dataSourceType} onChange={(e) => setDataSourceType(e.target.value)}>
                {allowedDataTypes.map((id) => (
                  <option key={id} value={id}>{DATA_SOURCE_TYPE_LABELS[id] || id}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Visibility</span>
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                {SCENARIO_VISIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Color</span>
              <input type="color" value={displayColor} onChange={(e) => setDisplayColor(e.target.value)} />
            </label>
            <label className="veps-span-2">
              <span>Description</span>
              <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="veps-span-2">
              <span>Risk label (subtitle on dashboard)</span>
              <input type="text" value={riskLabel} onChange={(e) => setRiskLabel(e.target.value)} />
            </label>
            {showStrategyNameField && dataSourceType !== 'nt_connector' ? (
              <label className="veps-span-2">
                <span>{dataSourceType === 'futures_snapshot' ? 'Futures strategy name' : 'Strategy / data name'}</span>
                {dataSourceType === 'futures_snapshot' ? (
                  <select value={strategyName} onChange={(e) => setStrategyName(e.target.value)}>
                    <option value="">Select futures strategy…</option>
                    {(futuresCatalogNames || []).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={strategyName} onChange={(e) => setStrategyName(e.target.value)} />
                )}
              </label>
            ) : null}
            {dataSourceType === 'webhook_tradingview' ? (
              <label className="veps-span-2">
                <span>TradingView strategy_id (optional)</span>
                <input
                  type="text"
                  value={webhookStrategyId}
                  onChange={(e) => setWebhookStrategyId(e.target.value)}
                />
              </label>
            ) : null}
            {dataSourceType === 'webhook_kairos' ? (
              <label className="veps-span-2">
                <span>Kairos symbol</span>
                <select value={kairosSymbol} onChange={(e) => setKairosSymbol(e.target.value)}>
                  {KAIROS_SYMBOL_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {dataSourceType === 'webhook_nt8_alert' ? (
              <Nt8AlertStrategyMappingFields
                nt8StrategyName={nt8StrategyName}
                onNt8StrategyNameChange={setNt8StrategyName}
                mappedFrom={mappedFrom}
                strategies={nt8StrategyOptions}
                loading={nt8AlertStrategies.loading}
                loadError={nt8AlertStrategies.loadError}
              />
            ) : null}
            {showNt8OverlayFields ? (
              <Nt8OverlaySleeveBuilder
                sleeves={nt8OverlaySleeves}
                onSleevesChange={setNt8OverlaySleeves}
                strategyOptions={nt8StrategyOptions}
                loading={nt8AlertStrategies.loading}
                loadError={nt8AlertStrategies.loadError}
                mappedFrom={mappedFrom}
              />
            ) : null}
            {isWebhookFlavor(dataSourceType) ? (
              <p className="veps-scenario-hint veps-span-2">{webhookHint}</p>
            ) : null}
            {dataSourceType === 'portfolio_blend' ? (
              <label className="veps-span-2">
                <span>Portfolio config key</span>
                <input type="text" value={portfolioKey} onChange={(e) => setPortfolioKey(e.target.value)} />
              </label>
            ) : null}
            {canReplaceCsv && dataSourceType !== 'nt_connector' ? (
              <>
                <label className="veps-span-2">
                  <span>{dataSourceType === 'webhook_nt8_alert' ? 'Replace TradesViz CSV (historical baseline)' : 'Replace CSV data (optional)'}</span>
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile} />
                  {csvFileName ? <small>{csvFileName}</small> : null}
                </label>
                {dataSourceType === 'equity_curve' || dataSourceType === 'webhook_kairos' ? (
                  <label>
                    <span>Equity column (optional)</span>
                    <input type="text" value={equityColumn} onChange={(e) => setEquityColumn(e.target.value)} />
                  </label>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="veps-scenario-actions">
            {status ? (
              <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span>
            ) : null}
            <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <a
              className="veps-scenario-data-link"
              href={`/admin/scenario-data?scenario=${encodeURIComponent(slug)}`}
            >
              Open in Strategy Data
            </a>
          </div>
        </>
      )}
    </div>
  );
}

function SortablePillOrderRow({ id, label }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`veps-pill-order-row${isDragging ? ' is-dragging' : ''}`}
    >
      <button
        type="button"
        className="veps-pill-order-grip"
        {...attributes}
        {...listeners}
        aria-label={`Drag ${label}`}
        title="Drag to reorder"
      >
        <IconGrip size={16} />
      </button>
      <span className="veps-pill-order-label">{label}</span>
    </div>
  );
}

function ControlsPillOrderEditor({ order, onChange, getLabel }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(order, oldIndex, newIndex));
  }, [onChange, order]);

  if (!order.length) return null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <div className="veps-pill-order-list">
          {order.map((id) => (
            <SortablePillOrderRow key={id} id={id} label={getLabel(id) || id} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function ScenarioSettingsSaveBar({
  status,
  saving,
  admin,
  isDirty,
  onSave,
  className = 'veps-scenario-actions',
}) {
  return (
    <div className={className}>
      {status ? (
        <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span>
      ) : null}
      {isDirty && !saving ? (
        <span className="veps-scenario-unsaved-hint">Unsaved changes</span>
      ) : null}
      <button
        type="button"
        className="veps-save-btn"
        onClick={onSave}
        disabled={saving || !admin}
      >
        {saving ? 'Saving…' : 'Save strategy settings'}
      </button>
    </div>
  );
}

// Default color for the global "Custom" line (yellow — distinct from Master
// Multi Account's purple). Mirrors DEFAULT_CUSTOM_LINE_COLOR on the dashboard.
const DEFAULT_CUSTOM_LINE_COLOR = '#eab308';

function ScenarioSettingsEditor({
  futuresCatalogNames,
  equityAlgoCatalog,
  onSaved,
  isFullAdmin = false,
  scenarioPillOrder = [],
  getScenarioLabel = (id) => id,
}) {
  const [admin, setAdmin] = useState(null);
  const [draft, setDraft] = useState({ futures: {}, stocks: {}, equityAlgos: {}, tests: {} });
  const [draftLabels, setDraftLabels] = useState({ futures: {}, stocks: {}, equityAlgos: {}, tests: {} });
  const [draftColors, setDraftColors] = useState({ futures: {}, stocks: {}, equityAlgos: {}, tests: {} });
  const [draftShowInCustom, setDraftShowInCustom] = useState({ futures: {}, stocks: {}, equityAlgos: {}, tests: {} });
  const [draftCustomColor, setDraftCustomColor] = useState(DEFAULT_CUSTOM_LINE_COLOR);
  const [draftPillOrder, setDraftPillOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [nt8OverlaySlug, setNt8OverlaySlug] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const futuresCatalogRef = useRef(futuresCatalogNames);
  const equityAlgoCatalogRef = useRef(equityAlgoCatalog);
  futuresCatalogRef.current = futuresCatalogNames;
  equityAlgoCatalogRef.current = equityAlgoCatalog;

  const dirtyRef = useRef(false);
  const suppressDirtyRef = useRef(false);
  const baselineRef = useRef(null);

  const catalogSignature = useMemo(
    () => `${futuresCatalogNames.join('\0')}||${equityAlgoCatalog.join('\0')}`,
    [futuresCatalogNames, equityAlgoCatalog],
  );

  const clearDirty = useCallback(() => {
    dirtyRef.current = false;
    setIsDirty(false);
  }, []);

  const applyAdminPayload = useCallback((data) => {
    const nextDraft = { futures: {}, stocks: {}, equityAlgos: {}, tests: {} };
    const nextDraftLabels = { futures: {}, stocks: {}, equityAlgos: {}, tests: {} };
    const nextDraftColors = { futures: {}, stocks: {}, equityAlgos: {}, tests: {} };
    const nextDraftShowInCustom = { futures: {}, stocks: {}, equityAlgos: {}, tests: {} };
    (data.futures || []).forEach((row) => {
      nextDraft.futures[row.key] = visibilityFromAdminRow(row);
      nextDraftLabels.futures[row.key] = row.displayLabel || row.label;
      nextDraftColors.futures[row.key] = row.displayColor || row.defaultColor;
      nextDraftShowInCustom.futures[row.key] = row.showInCustom !== false;
    });
    (data.stocks || []).forEach((row) => {
      nextDraft.stocks[row.key] = visibilityFromAdminRow(row);
      nextDraftLabels.stocks[row.key] = row.displayLabel || row.label;
      nextDraftColors.stocks[row.key] = row.displayColor || row.defaultColor;
      nextDraftShowInCustom.stocks[row.key] = row.showInCustom !== false;
    });
    (data.equityAlgos || []).forEach((row) => {
      nextDraft.equityAlgos[row.key] = visibilityFromAdminRow(row);
      nextDraftLabels.equityAlgos[row.key] = row.displayLabel || prettyStockAlgoLabel(row.label);
      nextDraftColors.equityAlgos[row.key] = row.displayColor || row.defaultColor;
      nextDraftShowInCustom.equityAlgos[row.key] = row.showInCustom !== false;
    });
    (data.tests || []).forEach((row) => {
      nextDraft.tests[row.key] = visibilityFromAdminRow(row);
      nextDraftLabels.tests[row.key] = row.displayLabel || row.label;
      nextDraftColors.tests[row.key] = row.displayColor || row.defaultColor;
      nextDraftShowInCustom.tests[row.key] = row.showInCustom !== false;
    });

    const nextCustomColor = normalizeHexColorInput(data.customColor || DEFAULT_CUSTOM_LINE_COLOR, DEFAULT_CUSTOM_LINE_COLOR);
    const savedOrder = Array.isArray(data.controlsPillOrder) && data.controlsPillOrder.length
      ? data.controlsPillOrder
      : null;
    const nextPillOrder = applyControlsPillOrder(scenarioPillOrder, savedOrder);

    suppressDirtyRef.current = true;
    setAdmin(data);
    setDraft(nextDraft);
    setDraftLabels(nextDraftLabels);
    setDraftColors(nextDraftColors);
    setDraftShowInCustom(nextDraftShowInCustom);
    setDraftCustomColor(nextCustomColor);
    setDraftPillOrder(nextPillOrder);
    baselineRef.current = JSON.stringify({
      draft: nextDraft,
      draftLabels: nextDraftLabels,
      draftColors: nextDraftColors,
      draftShowInCustom: nextDraftShowInCustom,
      draftCustomColor: nextCustomColor,
      draftPillOrder: nextPillOrder,
    });
    clearDirty();
    window.setTimeout(() => {
      suppressDirtyRef.current = false;
    }, 0);
  }, [clearDirty, scenarioPillOrder]);

  const loadAdmin = useCallback(async ({ force = false } = {}) => {
    if (dirtyRef.current && !force) return;

    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        API_ENDPOINTS.adminVectorBasketDisplay(
          futuresCatalogRef.current,
          equityAlgoCatalogRef.current,
        ),
        { headers: getBearerAuthHeaders() },
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to load strategy settings');
      }
      applyAdminPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load strategy settings');
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, [applyAdminPayload]);

  useEffect(() => {
    loadAdmin({ force: true });
  }, [loadAdmin]);

  const catalogInitializedRef = useRef(false);
  useEffect(() => {
    if (!catalogInitializedRef.current) {
      catalogInitializedRef.current = true;
      return;
    }
    loadAdmin({ force: false });
  }, [catalogSignature, loadAdmin]);

  useEffect(() => {
    if (suppressDirtyRef.current || !admin) return;
    const snap = JSON.stringify({
      draft, draftLabels, draftColors, draftShowInCustom, draftCustomColor, draftPillOrder,
    });
    if (!baselineRef.current) {
      baselineRef.current = snap;
      return;
    }
    if (snap !== baselineRef.current) {
      dirtyRef.current = true;
      setIsDirty(true);
    }
  }, [draft, draftLabels, draftColors, draftShowInCustom, draftCustomColor, draftPillOrder, admin]);

  const canEditRegistryRow = useCallback(
    (row) => isFullAdmin && row?.registryBacked,
    [isFullAdmin],
  );

  const canConfigureNt8Overlay = useCallback(
    (row) => isFullAdmin && row?.registryBacked
      && supportsNt8LiveOverlayStoredType(row?.dataSourceType, row?.dataSourceConfig),
    [isFullAdmin],
  );

  const handleSave = async () => {
    if (!admin) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const items = [
        ...(admin.futures || []).map((row) => {
          const visibility = normalizeScenarioVisibility(draft.futures[row.key]);
          return {
            category: 'futures',
            itemKey: row.key,
            visibility,
            enabled: visibility !== 'hidden',
            displayLabel: normalizeDisplayLabelForSave(draftLabels.futures[row.key], row.label),
            displayColor: normalizeDisplayColorForSave(draftColors.futures[row.key], row.defaultColor),
            showInCustom: draftShowInCustom.futures[row.key] !== false,
          };
        }),
        ...(admin.stocks || []).map((row) => {
          const visibility = normalizeScenarioVisibility(draft.stocks[row.key]);
          return {
            category: 'stocks',
            itemKey: row.key,
            visibility,
            enabled: visibility !== 'hidden',
            displayLabel: normalizeDisplayLabelForSave(draftLabels.stocks[row.key], row.label),
            displayColor: normalizeDisplayColorForSave(draftColors.stocks[row.key], row.defaultColor),
            showInCustom: draftShowInCustom.stocks[row.key] !== false,
          };
        }),
        ...(admin.equityAlgos || []).map((row) => {
          const visibility = normalizeScenarioVisibility(draft.equityAlgos[row.key]);
          return {
            category: 'equity_algos',
            itemKey: row.key,
            visibility,
            enabled: visibility !== 'hidden',
            displayLabel: normalizeDisplayLabelForSave(draftLabels.equityAlgos[row.key], prettyStockAlgoLabel(row.label)),
            displayColor: normalizeDisplayColorForSave(draftColors.equityAlgos[row.key], row.defaultColor),
            showInCustom: draftShowInCustom.equityAlgos[row.key] !== false,
          };
        }),
        ...(admin.tests || []).map((row) => {
          const visibility = normalizeScenarioVisibility(draft.tests[row.key]);
          return {
            category: 'tests',
            itemKey: row.key,
            visibility,
            enabled: visibility !== 'hidden',
            displayLabel: normalizeDisplayLabelForSave(draftLabels.tests[row.key], row.label),
            displayColor: normalizeDisplayColorForSave(draftColors.tests[row.key], row.defaultColor),
            showInCustom: draftShowInCustom.tests[row.key] !== false,
          };
        }),
      ];
      const resp = await fetch(API_ENDPOINTS.adminVectorBasketDisplaySave, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          items,
          customColor: draftCustomColor,
          controlsPillOrder: draftPillOrder,
          futuresCatalog: futuresCatalogRef.current,
          equityAlgos: equityAlgoCatalogRef.current,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to save strategy settings');
      }
      if (onSaved && data.enabled) onSaved(data.enabled);
      setStatus({ ok: true, msg: 'Saved — strategy pills and custom basket updated.' });
      await loadAdmin({ force: true });
    } catch (err) {
      setError(err.message || 'Failed to save strategy settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !admin) {
    return <p className="veps-scenario-intro">Loading strategy options…</p>;
  }

  if (nt8OverlaySlug) {
    return (
      <ScenarioNt8OverlayEditor
        slug={nt8OverlaySlug}
        onCancel={() => setNt8OverlaySlug(null)}
        onSaved={(scenario) => {
          loadAdmin({ force: true });
          if (onSaved) onSaved();
          if (scenario?.slug) setNt8OverlaySlug(null);
        }}
      />
    );
  }

  if (editingSlug) {
    return (
      <ScenarioEditEditor
        slug={editingSlug}
        futuresCatalogNames={futuresCatalogNames}
        onCancel={() => setEditingSlug(null)}
        onSaved={(scenario) => {
          loadAdmin({ force: true });
          if (onSaved) onSaved();
          if (scenario?.slug) setEditingSlug(null);
        }}
      />
    );
  }

  return (
    <div className="veps-scenario-editor">
      <p className="veps-scenario-intro">
        Set each strategy to Public (all members), Internal (admin / vector admin only), or Hidden
        to control which strategy pills appear on the dashboard.
        The Custom checkbox controls whether a strategy appears in the Custom composition grid
        (independent of Public / Internal / Hidden).
        Edit names and colors for both.
        {isFullAdmin ? (
          <> Use the gear to edit any strategy (CSV, data source, display fields). Use the lightning icon to quickly map NT8 live webhooks on top of CSV history.</>
        ) : null}
      </p>
      <ScenarioSettingsSaveBar
        status={status}
        saving={saving}
        admin={admin}
        isDirty={isDirty}
        onSave={handleSave}
        className="veps-scenario-actions veps-scenario-actions--top"
      />
      {error ? <p className="veps-scenario-error">{error}</p> : null}
      {admin ? (
        <>
          <div className="veps-scenario-section veps-pill-order-section">
            <h4>Controls widget order</h4>
            <p className="veps-scenario-hint">
              Drag to reorder the strategy pills on the Overview controls bar. Save to apply for everyone on this brand.
            </p>
            <ControlsPillOrderEditor
              order={draftPillOrder}
              onChange={setDraftPillOrder}
              getLabel={getScenarioLabel}
            />
          </div>
          <div className="veps-custom-color-row">
            <div className="veps-custom-color-text">
              <span className="veps-custom-color-title">
                <span
                  className="veps-custom-color-swatch"
                  style={{ background: normalizeHexColorInput(draftCustomColor, DEFAULT_CUSTOM_LINE_COLOR) }}
                  aria-hidden="true"
                />
                Custom line color
              </span>
              <span className="veps-custom-color-hint">
                Sets the &ldquo;Custom&rdquo; scenario line color for everyone. Save to apply.
              </span>
            </div>
            <div className="veps-custom-color-controls">
              <input
                type="color"
                className="veps-custom-color-input"
                value={normalizeHexColorInput(draftCustomColor, DEFAULT_CUSTOM_LINE_COLOR)}
                onChange={(e) => setDraftCustomColor(e.target.value)}
                aria-label="Custom line color"
                title="Custom line color (applies to all users)"
              />
              {normalizeHexColorInput(draftCustomColor, DEFAULT_CUSTOM_LINE_COLOR) !== DEFAULT_CUSTOM_LINE_COLOR ? (
                <button
                  type="button"
                  className="veps-custom-color-reset"
                  onClick={() => setDraftCustomColor(DEFAULT_CUSTOM_LINE_COLOR)}
                >
                  Reset to yellow
                </button>
              ) : null}
            </div>
          </div>
          <div className="veps-scenario-section">
            <h4>Stock options</h4>
            <div className="veps-scenario-list">
              {(admin.stocks || []).map((row) => (
                <ScenarioOptionRow
                  key={row.key}
                  visibilityValue={draft.stocks[row.key]}
                  onVisibilityChange={(visibility) => {
                    setDraft((prev) => ({
                      ...prev,
                      stocks: { ...prev.stocks, [row.key]: visibility },
                    }));
                  }}
                  labelValue={draftLabels.stocks[row.key] ?? row.label}
                  onLabelChange={(value) => {
                    setDraftLabels((prev) => ({
                      ...prev,
                      stocks: { ...prev.stocks, [row.key]: value },
                    }));
                  }}
                  colorValue={draftColors.stocks[row.key] ?? row.defaultColor}
                  onColorChange={(value) => {
                    setDraftColors((prev) => ({
                      ...prev,
                      stocks: { ...prev.stocks, [row.key]: value },
                    }));
                  }}
                  showInCustom={draftShowInCustom.stocks[row.key] !== false}
                  onShowInCustomChange={(value) => {
                    setDraftShowInCustom((prev) => ({
                      ...prev,
                      stocks: { ...prev.stocks, [row.key]: value },
                    }));
                  }}
                  defaultLabel={row.label}
                  slugName={row.key}
                  showEditButton={canEditRegistryRow(row)}
                  onEdit={canEditRegistryRow(row) ? () => setEditingSlug(row.key) : undefined}
                  showNt8OverlayButton={canConfigureNt8Overlay(row)}
                  onNt8Overlay={canConfigureNt8Overlay(row) ? () => setNt8OverlaySlug(row.key) : undefined}
                />
              ))}
            </div>
          </div>
          {(admin.equityAlgos || []).length > 0 ? (
            <div className="veps-scenario-section">
              <h4>Individual stock algos</h4>
              <div className="veps-scenario-list">
                {(admin.equityAlgos || []).map((row) => (
                  <ScenarioOptionRow
                    key={row.key}
                    visibilityValue={draft.equityAlgos[row.key]}
                    onVisibilityChange={(visibility) => {
                      setDraft((prev) => ({
                        ...prev,
                        equityAlgos: { ...prev.equityAlgos, [row.key]: visibility },
                      }));
                    }}
                    labelValue={draftLabels.equityAlgos[row.key] ?? prettyStockAlgoLabel(row.label)}
                    onLabelChange={(value) => {
                      setDraftLabels((prev) => ({
                        ...prev,
                        equityAlgos: { ...prev.equityAlgos, [row.key]: value },
                      }));
                    }}
                    colorValue={draftColors.equityAlgos[row.key] ?? row.defaultColor}
                    onColorChange={(value) => {
                      setDraftColors((prev) => ({
                        ...prev,
                        equityAlgos: { ...prev.equityAlgos, [row.key]: value },
                      }));
                    }}
                    showInCustom={draftShowInCustom.equityAlgos[row.key] !== false}
                    onShowInCustomChange={(value) => {
                      setDraftShowInCustom((prev) => ({
                        ...prev,
                        equityAlgos: { ...prev.equityAlgos, [row.key]: value },
                      }));
                    }}
                    defaultLabel={prettyStockAlgoLabel(row.label)}
                    slugName={row.key}
                    showEditButton={canEditRegistryRow(row)}
                    onEdit={canEditRegistryRow(row) ? () => setEditingSlug(row.key) : undefined}
                    showNt8OverlayButton={canConfigureNt8Overlay(row)}
                    onNt8Overlay={canConfigureNt8Overlay(row) ? () => setNt8OverlaySlug(row.key) : undefined}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <div className="veps-scenario-section">
            <h4>Futures strategies</h4>
            <div className="veps-scenario-list">
              {(admin.futures || []).map((row) => (
                <ScenarioOptionRow
                  key={row.key}
                  visibilityValue={draft.futures[row.key]}
                  onVisibilityChange={(visibility) => {
                    setDraft((prev) => ({
                      ...prev,
                      futures: { ...prev.futures, [row.key]: visibility },
                    }));
                  }}
                  labelValue={draftLabels.futures[row.key] ?? row.label}
                  onLabelChange={(value) => {
                    setDraftLabels((prev) => ({
                      ...prev,
                      futures: { ...prev.futures, [row.key]: value },
                    }));
                  }}
                  colorValue={draftColors.futures[row.key] ?? row.defaultColor}
                  onColorChange={(value) => {
                    setDraftColors((prev) => ({
                      ...prev,
                      futures: { ...prev.futures, [row.key]: value },
                    }));
                  }}
                  showInCustom={draftShowInCustom.futures[row.key] !== false}
                  onShowInCustomChange={(value) => {
                    setDraftShowInCustom((prev) => ({
                      ...prev,
                      futures: { ...prev.futures, [row.key]: value },
                    }));
                  }}
                  defaultLabel={row.label}
                  slugName={row.key}
                  showEditButton={canEditRegistryRow(row)}
                  onEdit={canEditRegistryRow(row) ? () => setEditingSlug(row.key) : undefined}
                  showNt8OverlayButton={canConfigureNt8Overlay(row)}
                  onNt8Overlay={canConfigureNt8Overlay(row) ? () => setNt8OverlaySlug(row.key) : undefined}
                />
              ))}
            </div>
          </div>
          {(admin.tests || []).length > 0 ? (
            <div className="veps-scenario-section">
              <h4>Test</h4>
              <div className="veps-scenario-list">
                {(admin.tests || []).map((row) => (
                  <ScenarioOptionRow
                    key={row.key}
                    visibilityValue={draft.tests[row.key]}
                    onVisibilityChange={(visibility) => {
                      setDraft((prev) => ({
                        ...prev,
                        tests: { ...prev.tests, [row.key]: visibility },
                      }));
                    }}
                    labelValue={draftLabels.tests[row.key] ?? row.label}
                    onLabelChange={(value) => {
                      setDraftLabels((prev) => ({
                        ...prev,
                        tests: { ...prev.tests, [row.key]: value },
                      }));
                    }}
                    colorValue={draftColors.tests[row.key] ?? row.defaultColor}
                    onColorChange={(value) => {
                      setDraftColors((prev) => ({
                        ...prev,
                        tests: { ...prev.tests, [row.key]: value },
                      }));
                    }}
                    showInCustom={draftShowInCustom.tests[row.key] !== false}
                    onShowInCustomChange={(value) => {
                      setDraftShowInCustom((prev) => ({
                        ...prev,
                        tests: { ...prev.tests, [row.key]: value },
                      }));
                    }}
                    defaultLabel={row.label}
                    slugName={row.key}
                    showEditButton={canEditRegistryRow(row)}
                    onEdit={canEditRegistryRow(row) ? () => setEditingSlug(row.key) : undefined}
                    showNt8OverlayButton={canConfigureNt8Overlay(row)}
                    onNt8Overlay={canConfigureNt8Overlay(row) ? () => setNt8OverlaySlug(row.key) : undefined}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      <ScenarioSettingsSaveBar
        status={status}
        saving={saving}
        admin={admin}
        isDirty={isDirty}
        onSave={handleSave}
      />
    </div>
  );
}

/** Benchmark tab — which pills show, history start, colors, default selection. */
function BenchmarkSettingsEditor({ currentSettings, onSaved }) {
  const effective = useMemo(
    () => normalizeBenchmarkSettings(currentSettings),
    [currentSettings],
  );
  const [draft, setDraft] = useState(effective);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => { setDraft(effective); }, [effective]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(effective), [draft, effective]);

  const toggleEnabled = (id) => {
    setDraft((prev) => {
      const enabled = new Set(prev.enabled);
      const defaultSelected = new Set(prev.defaultSelected);
      if (enabled.has(id)) {
        enabled.delete(id);
        defaultSelected.delete(id);
      } else {
        enabled.add(id);
      }
      const nextEnabled = BENCHMARK_CATALOG.map((b) => b.id).filter((bid) => enabled.has(bid));
      let nextSelected = [...defaultSelected].filter((bid) => enabled.has(bid));
      if (!nextSelected.length && nextEnabled.length) nextSelected = [nextEnabled[0]];
      return {
        ...prev,
        enabled: nextEnabled,
        defaultSelected: nextSelected,
      };
    });
  };

  const toggleDefaultSelected = (id) => {
    setDraft((prev) => {
      if (!prev.enabled.includes(id)) return prev;
      const selected = new Set(prev.defaultSelected);
      if (selected.has(id)) selected.delete(id);
      else selected.add(id);
      const nextSelected = BENCHMARK_CATALOG.map((b) => b.id).filter((bid) => selected.has(bid));
      return {
        ...prev,
        defaultSelected: nextSelected.length ? nextSelected : [id],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const resp = await fetch(API_ENDPOINTS.adminBenchmarkSettings, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ settings: draft }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed.');
      const next = normalizeBenchmarkSettings(data.settings);
      setDraft(next);
      if (onSaved) onSaved(next);
      setSavedMsg('Saved — Overview benchmarks update for this brand.');
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-method veps-benchmark-settings">
      <h4 className="veps-section-label">Overview benchmarks</h4>
      <p className="veps-intro">
        Control which benchmark pills appear on the growth chart, how far back their history is
        fetched, chart line colors, and which benchmarks are selected by default for new visitors.
        Settings apply to this brand only.
      </p>

      <label className="veps-benchmark-date-field">
        <span>History start date</span>
        <input
          type="date"
          value={draft.historyStartDate}
          min="1990-01-01"
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDraft((prev) => ({
            ...prev,
            historyStartDate: e.target.value || DEFAULT_HISTORY_START,
          }))}
        />
        <span className="veps-benchmark-date-hint">
          Strategy curves, benchmark lines, comparison tables, calendar, and list of trades only load data from this date forward.
        </span>
      </label>

      <div className="veps-benchmark-table-wrap">
        <table className="veps-benchmark-table">
          <thead>
            <tr>
              <th>Show</th>
              <th>Default on chart</th>
              <th>Benchmark</th>
              <th>Line color</th>
            </tr>
          </thead>
          <tbody>
            {BENCHMARK_CATALOG.map((entry) => {
              const isEnabled = draft.enabled.includes(entry.id);
              const isDefault = draft.defaultSelected.includes(entry.id);
              return (
                <tr key={entry.id} className={isEnabled ? '' : 'veps-benchmark-row-off'}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => toggleEnabled(entry.id)}
                      aria-label={`Show ${entry.label}`}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={isDefault}
                      disabled={!isEnabled}
                      onChange={() => toggleDefaultSelected(entry.id)}
                      aria-label={`Default select ${entry.label}`}
                    />
                  </td>
                  <td>
                    <strong>{entry.label}</strong>
                    <span className="veps-benchmark-ticker">{entry.shortLabel}</span>
                  </td>
                  <td>
                    <input
                      type="color"
                      className="veps-benchmark-color"
                      value={draft.colors[entry.id] || '#94a3b8'}
                      disabled={!isEnabled}
                      onChange={(e) => setDraft((prev) => ({
                        ...prev,
                        colors: { ...prev.colors, [entry.id]: e.target.value },
                      }))}
                      aria-label={`Color for ${entry.label}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="veps-save-wrap">
        {error ? <span className="veps-status err">{error}</span> : null}
        {savedMsg && !dirty ? <span className="veps-status ok">{savedMsg}</span> : null}
        <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save benchmark settings'}
        </button>
      </div>
    </div>
  );
}

/** Method tab — calendar-month % per strategy category (not per strategy). */
function MonthlyReturnMethodEditor({ currentMethods, onSaved }) {
  const effective = useMemo(
    () => normalizeMonthlyReturnMethods(currentMethods),
    [currentMethods],
  );
  const [selected, setSelected] = useState(effective);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => { setSelected(effective); }, [effective]);

  const dirty = MONTHLY_METHOD_CATEGORY_ORDER.some((id) => selected[id] !== effective[id]);

  const handleSave = async () => {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const resp = await fetch(API_ENDPOINTS.adminMonthlyReturnMethods, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ methods: selected }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed.');
      const next = normalizeMonthlyReturnMethods(data.methods);
      setSelected(next);
      if (onSaved) onSaved(next);
      setSavedMsg('Saved — monthly % now follows these category settings.');
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-method">
      <h4 className="veps-section-label">Monthly returns</h4>
      <p className="veps-intro">
        Choose how <strong>calendar-month %</strong> is calculated — one choice per category, not per
        strategy. Futures stay on a flat book; portfolio stocks (Kairos, Equity Portfolios, Congress
        Trader-style curves) can use true account %. Benchmarks (SPY) always use account %.
      </p>
      {MONTHLY_METHOD_CATEGORY_ORDER.map((categoryId) => {
        const cat = MONTHLY_METHOD_CATEGORIES[categoryId];
        return (
          <div key={categoryId} className="veps-monthly-cat">
            <div className="veps-monthly-cat-head">
              <strong>{cat.label}</strong>
              <span>{cat.hint}</span>
            </div>
            <div className="veps-method-grid">
              {['fixed', 'compound'].map((methodId) => {
                const m = MONTHLY_RETURN_METHODS[methodId];
                const isLive = effective[categoryId] === methodId;
                const isOn = selected[categoryId] === methodId;
                return (
                  <label key={methodId} className={`veps-method-card ${isOn ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name={`monthly-method-${categoryId}`}
                      value={methodId}
                      checked={isOn}
                      onChange={() => setSelected((prev) => ({ ...prev, [categoryId]: methodId }))}
                    />
                    <span className="veps-method-card-title">
                      {m.label}
                      {isLive ? <span className="veps-method-live"> · live</span> : null}
                    </span>
                    <span className="veps-method-card-tag">{m.tagline}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="veps-save-wrap">
        {error ? <span className="veps-status err">{error}</span> : null}
        {savedMsg && !dirty ? <span className="veps-status ok">{savedMsg}</span> : null}
        <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save monthly methods'}
        </button>
      </div>
    </div>
  );
}

/** Method tab — pick the global calculation method + see exactly what math it does. */
function MethodEditor({ currentMethod, onSaved }) {
  const effective = currentMethod || DEFAULT_EQUITY_PORTFOLIO_METHOD;
  const [selected, setSelected] = useState(effective);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => { setSelected(effective); }, [effective]);

  const def = EQUITY_PORTFOLIO_METHODS[selected] || EQUITY_PORTFOLIO_METHODS[DEFAULT_EQUITY_PORTFOLIO_METHOD];
  const dirty = selected !== effective;

  const handleSave = async () => {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const resp = await fetch(API_ENDPOINTS.adminEquityPortfolioMethod, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ method: selected }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed.');
      if (onSaved && data.configs) onSaved(data.configs);
      setSavedMsg('Saved — every Equity Portfolio now uses this method.');
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-method">
      <p className="veps-intro">
        Choose how the computed Equity Portfolios&apos; curves and metrics are calculated. Two axes:
        <strong> weighting</strong> (how each stock&apos;s share is set) and <strong>compounding</strong>
        {' '}(whether profits reinvest). Applies to the <strong>Equity Portfolio 1× / 2.25×</strong> books; the
        {' '}<strong>OG</strong> portfolios mirror Fyntrak&apos;s ingested curve and ignore this. Admin / Brand Admin only.
      </p>
      <div className="veps-method-grid">
        {EQUITY_PORTFOLIO_METHOD_ORDER.map((id) => {
          const m = EQUITY_PORTFOLIO_METHODS[id];
          return (
            <label key={id} className={`veps-method-card ${selected === id ? 'active' : ''}`}>
              <input
                type="radio"
                name="equity-method"
                value={id}
                checked={selected === id}
                onChange={() => setSelected(id)}
              />
              <span className="veps-method-card-title">
                {m.label}
                {id === effective ? <span className="veps-method-live"> · live</span> : null}
              </span>
              <span className="veps-method-card-tag">{m.tagline}</span>
            </label>
          );
        })}
      </div>

      <div className="veps-method-detail">
        <h4>{def.label} — the math</h4>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Weighting</span>
          <p>{def.weightingDetail}</p>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Compounding</span>
          <p>{def.compoundingDetail}</p>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Formula</span>
          <code className="veps-method-formula">{def.formula}</code>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Worked example</span>
          <p>{def.example}</p>
        </div>
      </div>

      <div className="veps-save-wrap">
        {error ? <span className="veps-status err">{error}</span> : null}
        {savedMsg && !dirty ? <span className="veps-status ok">{savedMsg}</span> : null}
        <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save method'}
        </button>
      </div>
    </div>
  );
}

/** Custom-scenario section of the Method tab — how the blend handles strategies
 *  whose data starts on different dates. Per-user (localStorage); applies instantly. */
function CustomBlendStartBasisEditor({ value, onChange }) {
  const effective = CUSTOM_BLEND_START_BASES[value] ? value : DEFAULT_CUSTOM_BLEND_START_BASIS;
  const def = CUSTOM_BLEND_START_BASES[effective];

  return (
    <div className="veps-method">
      <p className="veps-intro">
        Choose how the <strong>Custom</strong> scenario blends strategies whose data starts on
        {' '}<strong>different dates</strong> (e.g. a 2020 strategy next to one that went live this year).
        Each strategy still gets its Weight % of the capital base and compounds on its own returns —
        this only controls what happens before a younger strategy&apos;s data begins. Saved on this
        device and applied immediately.
      </p>
      <div className="veps-method-grid">
        {CUSTOM_BLEND_START_BASIS_ORDER.map((id) => {
          const m = CUSTOM_BLEND_START_BASES[id];
          return (
            <label key={id} className={`veps-method-card ${effective === id ? 'active' : ''}`}>
              <input
                type="radio"
                name="custom-blend-start-basis"
                value={id}
                checked={effective === id}
                onChange={() => onChange(id)}
              />
              <span className="veps-method-card-title">
                {m.label}
                {id === effective ? <span className="veps-method-live"> · live</span> : null}
              </span>
              <span className="veps-method-card-tag">{m.tagline}</span>
            </label>
          );
        })}
      </div>

      <div className="veps-method-detail">
        <h4>{def.label} — the math</h4>
        <div className="veps-method-block">
          <span className="veps-method-block-head">How it works</span>
          <p>{def.detail}</p>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Formula</span>
          <code className="veps-method-formula">{def.formula}</code>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Worked example</span>
          <p>{def.example}</p>
        </div>
      </div>
    </div>
  );
}

/** Leverage-model section of the Method tab — Fyntrak-linear vs real compounded. */
function LeverageModeEditor({ currentLeverageMode, onSaved }) {
  const effective = currentLeverageMode || DEFAULT_EQUITY_PORTFOLIO_LEVERAGE_MODE;
  const [selected, setSelected] = useState(effective);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => { setSelected(effective); }, [effective]);

  const def = EQUITY_PORTFOLIO_LEVERAGE_MODES[selected]
    || EQUITY_PORTFOLIO_LEVERAGE_MODES[DEFAULT_EQUITY_PORTFOLIO_LEVERAGE_MODE];
  const dirty = selected !== effective;

  const handleSave = async () => {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const resp = await fetch(API_ENDPOINTS.adminEquityPortfolioLeverageMode, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ leverageMode: selected }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed.');
      if (onSaved && data.configs) onSaved(data.configs);
      setSavedMsg('Saved — leverage model updated for the 2.25× portfolios.');
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="veps-method veps-leverage-section">
      <h4 className="veps-leverage-heading">Leverage model</h4>
      <p className="veps-intro">
        How the <strong>2.25× portfolios</strong> apply leverage (the 1× books are identical either way).
        <strong> Fyntrak linear</strong> matches Fyntrak&apos;s softer 2.25× (annualized × 2.25);
        <strong> Real compounded</strong> is the mathematically honest, hotter result.
      </p>
      <div className="veps-method-grid">
        {EQUITY_PORTFOLIO_LEVERAGE_MODE_ORDER.map((id) => {
          const m = EQUITY_PORTFOLIO_LEVERAGE_MODES[id];
          return (
            <label key={id} className={`veps-method-card ${selected === id ? 'active' : ''}`}>
              <input
                type="radio"
                name="equity-leverage-mode"
                value={id}
                checked={selected === id}
                onChange={() => setSelected(id)}
              />
              <span className="veps-method-card-title">
                {m.label}
                {id === effective ? <span className="veps-method-live"> · live</span> : null}
              </span>
              <span className="veps-method-card-tag">{m.tagline}</span>
            </label>
          );
        })}
      </div>

      <div className="veps-method-detail">
        <h4>{def.label} — the math</h4>
        <div className="veps-method-block">
          <span className="veps-method-block-head">How it works</span>
          <p>{def.detail}</p>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Formula</span>
          <code className="veps-method-formula">{def.formula}</code>
        </div>
        <div className="veps-method-block">
          <span className="veps-method-block-head">Worked example</span>
          <p>{def.example}</p>
        </div>
      </div>

      <div className="veps-save-wrap">
        {error ? <span className="veps-status err">{error}</span> : null}
        {savedMsg && !dirty ? <span className="veps-status ok">{savedMsg}</span> : null}
        <button type="button" className="veps-save-btn" onClick={handleSave} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save leverage model'}
        </button>
      </div>
    </div>
  );
}

function Nt8AccountSelect({ options, value, onChange, placeholder = 'Select an account…', disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find((opt) => opt.id === value) || null,
    [options, value],
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    }
    function handleEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className={`veps-select${open ? ' open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="veps-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled || options.length === 0}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={selected ? 'veps-select-value' : 'veps-select-placeholder'}>
          {selected ? selected.name : placeholder}
        </span>
        <IconChevronDown size={16} className="veps-select-chevron" />
      </button>
      {open && options.length > 0 && (
        <div id={listId} className="veps-select-menu" role="listbox">
          {options.map((opt) => {
            const active = opt.id === value;
            return (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={active}
                className={`veps-select-option${active ? ' active' : ''}`}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
              >
                <span>{opt.name}</span>
                {active ? <IconCheck size={14} className="veps-select-option-check" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function createEmptyMappingRow() {
  return {
    rowId: `draft:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    scenarioSlug: '',
    connectionId: '',
    persisted: false,
  };
}

function rowsFromBoundScenarios(scenarios) {
  return (scenarios || [])
    .filter((s) => s.mappable !== false && s.connectionId)
    .map((s) => ({
      rowId: `scenario:${s.slug}`,
      scenarioSlug: s.slug,
      connectionId: String(s.connectionId),
      persisted: true,
    }));
}

function Nt8AccountEditor({ onSaved, ntConnectorDownload, active = false }) {
  const [loading, setLoading] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [ntAccounts, setNtAccounts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [rows, setRows] = useState([createEmptyMappingRow()]);
  const [savingRowId, setSavingRowId] = useState(null);
  const [status, setStatus] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(API_ENDPOINTS.adminNt8Settings, { headers: getBearerAuthHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Failed to load NT8 settings');
      const nextScenarios = data.scenarios || [];
      setScenarios(nextScenarios);
      setNtAccounts(data.accounts || []);
      setMappings(data.mappings || []);
      setRows((prev) => {
        const drafts = prev.filter((r) => !r.persisted);
        const saved = rowsFromBoundScenarios(nextScenarios);
        const savedSlugs = new Set(saved.map((r) => r.scenarioSlug));
        const remainingDrafts = drafts.filter((d) => !d.scenarioSlug || !savedSlugs.has(d.scenarioSlug));
        return saved.length || remainingDrafts.length
          ? [...saved, ...remainingDrafts]
          : [createEmptyMappingRow()];
      });
    } catch (e) {
      setScenarios([]);
      setNtAccounts([]);
      setMappings([]);
      setRows([createEmptyMappingRow()]);
      setStatus({ ok: false, msg: e.message || 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) loadSettings(); }, [active, loadSettings]);

  const accountOptions = useMemo(
    () => (ntAccounts || []).map((a) => ({ id: String(a.id), name: a.accountName })),
    [ntAccounts],
  );

  const scenarioOptions = useMemo(
    () => (scenarios || []).map((s) => ({
      id: s.slug,
      name: s.displayName,
      connectionId: s.connectionId ? String(s.connectionId) : '',
      dataSourceType: s.dataSourceType,
      mappable: s.mappable !== false,
      isSystem: Boolean(s.isSystem),
    })),
    [scenarios],
  );

  const mappableScenarioOptions = useMemo(
    () => scenarioOptions.filter((s) => s.mappable),
    [scenarioOptions],
  );

  const boundScenarios = useMemo(
    () => scenarioOptions.filter((s) => s.connectionId),
    [scenarioOptions],
  );

  const updateRow = useCallback((rowId, patch) => {
    setRows((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  }, []);

  const addMappingRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyMappingRow()]);
  }, []);

  const removeDraftRow = useCallback((rowId) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.rowId !== rowId);
      return next.length ? next : [createEmptyMappingRow()];
    });
  }, []);

  const scenarioOptionsForRow = useCallback((row) => {
    const usedElsewhere = new Set(
      rows
        .filter((r) => r.rowId !== row.rowId && r.scenarioSlug)
        .map((r) => r.scenarioSlug),
    );
    return mappableScenarioOptions
      .filter((s) => !usedElsewhere.has(s.id) || s.id === row.scenarioSlug)
      .map((s) => ({
        id: s.id,
        name: s.connectionId && s.id !== row.scenarioSlug ? `${s.name} (connected)` : s.name,
      }));
  }, [rows, mappableScenarioOptions]);

  const handleSaveRow = async (rowId) => {
    const row = rows.find((r) => r.rowId === rowId);
    if (!row?.scenarioSlug) {
      setStatus({ ok: false, msg: 'Select a strategy for this row.' });
      return;
    }
    if (!row.connectionId) {
      setStatus({ ok: false, msg: 'Select an NT8 account for this row.' });
      return;
    }
    const scenarioLabel = scenarioOptions.find((s) => s.id === row.scenarioSlug)?.name || row.scenarioSlug;
    setSavingRowId(rowId);
    setStatus(null);
    try {
      const resp = await fetch(API_ENDPOINTS.adminNt8Settings, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          scenarioSlug: row.scenarioSlug,
          connectionId: row.connectionId,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) throw new Error(data.error || 'Save failed.');
      await loadSettings();
      if (onSaved) onSaved();
      setStatus({
        ok: true,
        msg: `Mapped ${data.accountName || 'account'} to “${scenarioLabel}”.`,
      });
    } catch (e) {
      setStatus({ ok: false, msg: e.message || 'Save failed.' });
    } finally {
      setSavingRowId(null);
    }
  };

  return (
    <div className="veps-scenario-mappings">
      <h4 className="veps-portfolio-title">Connect account to strategy</h4>
      <p className="veps-mapping-intro">
        Map synced NinjaTrader accounts to dashboard scenarios. Each row saves independently;
        the scenario&apos;s data source becomes <strong>NT8 Connector (live sync)</strong>.
      </p>
      <div className="veps-connector-help veps-connector-help--top">
        <button type="button" className="vector-capital-live-link" onClick={loadSettings} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh accounts'}
        </button>
        {ntConnectorDownload ? (
          <a className="vector-capital-live-download" href={ntConnectorDownload}>Download connector v2</a>
        ) : null}
      </div>
      {loading ? (
        <p className="veps-mapping-hint">Loading accounts and strategies…</p>
      ) : (
        <>
          <div className="veps-nt-mapping-rows-wrap">
            <div className="veps-nt-mapping-rows">
              {rows.map((row, index) => {
                const rowScenarioOptions = scenarioOptionsForRow(row);
                const rowSaving = savingRowId === row.rowId;
                const canRemove = !row.persisted && rows.length > 1;
                return (
                  <div key={row.rowId} className="veps-nt-mapping-row">
                    <div className="veps-mapping-field">
                      {index === 0 ? <span className="veps-mapping-field-label">NT8 account</span> : null}
                      <Nt8AccountSelect
                        options={accountOptions}
                        value={row.connectionId}
                        onChange={(value) => updateRow(row.rowId, { connectionId: value })}
                        placeholder={accountOptions.length ? 'Select an account…' : 'No synced accounts yet'}
                        disabled={!accountOptions.length || rowSaving}
                      />
                    </div>
                    <div className="veps-mapping-field">
                      {index === 0 ? <span className="veps-mapping-field-label">Strategy</span> : null}
                      <Nt8AccountSelect
                        options={rowScenarioOptions}
                        value={row.scenarioSlug}
                        onChange={(value) => updateRow(row.rowId, { scenarioSlug: value })}
                        placeholder={
                          mappableScenarioOptions.length
                            ? 'Select a strategy…'
                            : 'Create a strategy in Create Strategy first'
                        }
                        disabled={!mappableScenarioOptions.length || rowSaving}
                      />
                    </div>
                    <div className="veps-nt-mapping-row-actions">
                      <button
                        type="button"
                        className="veps-save-btn veps-save-btn--compact"
                        onClick={() => handleSaveRow(row.rowId)}
                        disabled={rowSaving || !row.scenarioSlug || !row.connectionId}
                      >
                        {rowSaving ? 'Saving…' : 'Save'}
                      </button>
                      {canRemove ? (
                        <button
                          type="button"
                          className="veps-nt-mapping-remove"
                          onClick={() => removeDraftRow(row.rowId)}
                          disabled={rowSaving}
                          aria-label="Remove row"
                          title="Remove row"
                        >
                          <IconTrash size={15} color="currentColor" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            className="veps-nt-add-mapping-btn"
            onClick={addMappingRow}
            disabled={loading || !mappableScenarioOptions.length}
          >
            <IconPlus size={16} color="currentColor" />
            Add mapping
          </button>
          {!accountOptions.length ? (
            <p className="veps-mapping-hint">
              No synced accounts yet. Generate a key above, install the connector in NinjaTrader, and push data.
            </p>
          ) : null}
          {!mappableScenarioOptions.length ? (
            <p className="veps-mapping-hint">
              No scenarios yet. Create one in the <strong>Create Strategy</strong> tab first (any data source),
              then return here to attach your NT8 account. System presets like MMA Test cannot be remapped.
            </p>
          ) : null}
          {boundScenarios.length ? (
            <div className="veps-nt-bindings">
              <span className="veps-mapping-field-label">Current mappings</span>
              <ul className="veps-nt-bindings-list">
                {boundScenarios.map((s) => {
                  const acct = ntAccounts.find((a) => String(a.id) === s.connectionId);
                  const mapping = mappings.find((m) => m.scenarioSlug === s.id);
                  const lastSync = acct?.lastSyncAt
                    ? new Date(acct.lastSyncAt).toLocaleString()
                    : null;
                  const mappedAt = mapping?.updatedAt
                    ? new Date(mapping.updatedAt).toLocaleString()
                    : null;
                  const tradeCount = acct?.tradeCount ?? 0;
                  return (
                    <li key={s.id}>
                      <strong>{s.name}</strong>
                      <span> → {acct?.accountName || acct?.name || `Account #${s.connectionId}`}</span>
                      {acct ? (
                        <span className="veps-nt-binding-meta">
                          {' '}
                          ({tradeCount} trade{tradeCount === 1 ? '' : 's'}
                          {lastSync ? `, last sync ${lastSync}` : ', not synced yet'}
                          {mappedAt ? `, mapped ${mappedAt}` : ''})
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </>
      )}
      {status ? <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span> : null}
    </div>
  );
}

function VectorEquityPortfolioSettings({
  userEmail,
  configs,
  onSaved,
  futuresCatalogNames = [],
  equityAlgoCatalog = [],
  onScenarioDisplaySaved,
  ntConnectorDownload,
  isFullAdmin = false,
  customBlendStartBasis = DEFAULT_CUSTOM_BLEND_START_BASIS,
  onCustomBlendStartBasisChange,
  scenarioPillOrder = [],
  getScenarioLabel,
  monthlyReturnMethods = DEFAULT_MONTHLY_RETURN_METHODS,
  onMonthlyReturnMethodsSaved,
  benchmarkSettings,
  onBenchmarkSettingsSaved,
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  const adminOnlyTabs = useMemo(() => new Set(['create', 'nt8']), []);

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => (adminOnlyTabs.has(tab.id) ? isFullAdmin : true)),
    [adminOnlyTabs, isFullAdmin],
  );

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  // Fall back to the first tab this viewer can actually see, rather than a
  // hardcoded one — otherwise reordering TABS silently sends non-admins to
  // whichever tab the constant happened to name.
  useEffect(() => {
    if (!isFullAdmin && adminOnlyTabs.has(activeTab)) {
      setActiveTab(visibleTabs[0]?.id);
    }
  }, [isFullAdmin, activeTab, adminOnlyTabs, visibleTabs]);

  const saveOne = async (portfolioKey, values) => {
    const resp = await fetch(API_ENDPOINTS.adminEquityPortfolioConfig, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
      body: JSON.stringify({ portfolioKey, ...values }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.success) {
      throw new Error(data.error || 'Save failed.');
    }
    if (onSaved && data.configs) onSaved(data.configs);
  };

  return (
    <>
      <button
        type="button"
        className="veps-trigger"
        onClick={() => setExpanded(true)}
        title="Strategy settings (admin)"
      >
        <IconSettings size={14} color="currentColor" />
        Settings
      </button>

      {expanded && (
        <div
          className="veps-modal-overlay"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setExpanded(false); }}
        >
          <div className="veps-modal veps-modal--wide" role="dialog" aria-modal="true" aria-label="Settings">
            <div className="veps-modal-head">
              <div>
                <span className="vector-capital-panel-eyebrow">Admin</span>
                <h3>Settings</h3>
              </div>
              <button
                type="button"
                className="veps-modal-close"
                onClick={() => setExpanded(false)}
                aria-label="Close"
              >
                <IconX size={18} color="currentColor" />
              </button>
            </div>

            <div className="veps-tabs" role="tablist" aria-label="Settings sections">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`veps-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'portfolio' ? (
              <>
            <p className="veps-intro">
              Configure each Equity Portfolio. Saving persists the change and the Equity Portfolio strategies
              update immediately. Admin / Brand Admin only.
            </p>
            <PortfolioCalendarExplainer />
                {configs ? (
              <>
                <h4 className="veps-section-label">Equity Portfolios</h4>
                <div className="veps-grid">
                  {PORTFOLIOS.filter((p) => p.group === 'equity').map((p) => (
                    configs[p.key] ? (
                      <PortfolioEditor
                        key={p.key}
                        portfolioKey={p.key}
                        title={p.title}
                        config={configs[p.key]}
                        equityAlgoCatalog={equityAlgoCatalog}
                        onSave={(values) => saveOne(p.key, values)}
                      />
                    ) : null
                  ))}
                </div>
                <h4 className="veps-section-label">OG Portfolios (Fyntrak mirror)</h4>
                <div className="veps-grid">
                  {PORTFOLIOS.filter((p) => p.group === 'og').map((p) => (
                    configs[p.key] ? (
                      <PortfolioEditor
                        key={p.key}
                        portfolioKey={p.key}
                        title={p.title}
                        config={configs[p.key]}
                        equityAlgoCatalog={equityAlgoCatalog}
                        isOgPortfolio
                        onSave={(values) => saveOne(p.key, values)}
                      />
                    ) : null
                  ))}
                </div>
              </>
                ) : (
                  <p className="veps-mapping-hint">Loading portfolio settings…</p>
                )}
              </>
            ) : activeTab === 'benchmarks' ? (
              <BenchmarkSettingsEditor
                currentSettings={benchmarkSettings}
                onSaved={onBenchmarkSettingsSaved}
              />
            ) : activeTab === 'method' ? (
              <>
                <MonthlyReturnMethodEditor
                  currentMethods={monthlyReturnMethods}
                  onSaved={onMonthlyReturnMethodsSaved}
                />
                <MethodEditor
                  currentMethod={configs?.portfolio_1x?.method}
                  onSaved={onSaved}
                />
                <LeverageModeEditor
                  currentLeverageMode={configs?.portfolio_1x?.leverageMode}
                  onSaved={onSaved}
                />
                {onCustomBlendStartBasisChange ? (
                  <CustomBlendStartBasisEditor
                    value={customBlendStartBasis}
                    onChange={onCustomBlendStartBasisChange}
                  />
                ) : null}
              </>
            ) : activeTab === 'create' ? (
              <ScenarioCreatorEditor
                futuresCatalogNames={futuresCatalogNames}
                equityAlgoCatalog={equityAlgoCatalog}
                onCreated={onScenarioDisplaySaved}
              />
            ) : activeTab === 'nt8' ? (
              <div className="veps-nt8-tab-scroll">
                <p className="veps-intro">
                  Generate connector API keys and map the NT8 live account for the connector push feed.
                </p>
                <ConnectorApiKeyManager userEmail={userEmail} active={expanded} />
                <Nt8AccountEditor
                  active={activeTab === 'nt8'}
                  onSaved={onScenarioDisplaySaved}
                  ntConnectorDownload={ntConnectorDownload}
                />
              </div>
            ) : (
              <ScenarioSettingsEditor
                futuresCatalogNames={futuresCatalogNames}
                equityAlgoCatalog={equityAlgoCatalog}
                onSaved={onScenarioDisplaySaved}
                isFullAdmin={isFullAdmin}
                scenarioPillOrder={scenarioPillOrder}
                getScenarioLabel={getScenarioLabel}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VectorEquityPortfolioSettings;
